// @flow
import React, { useCallback, useContext, useMemo } from 'react';
import { SPOrgContext } from '@streaming-projects/orgs/contexts/SPOrgContext';
import { FieldArrayGrid, FieldArrayGridConfigItemContext } from '@src/formik-utils/FieldArrayGrid';
import {
  CONNECTOR_SMT_TYPE_BACKEND_NAME,
  CUSTOM_DISCOUNTS_JSON_BACKEND_NAME,
  METRICS_LEVEL_DISCOUNTS_CONFIG_ARRAY_BACKEND_NAME,
  METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_ARRAY_BACKEND_NAME,
  METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_NAME_BACKEND_NAME,
  RATE_CARD_BACKEND_NAME,
} from '@src/constants';
import { DataFetcherContainer } from '@presentational/DataFetcherContainer';
import { useGetDependentDimensionsQuery } from '@src/service-definitions/estimates';
import { getDependentDimensionsColumnConfig } from '@components/custom-discounts/utils';
import { CommitMetricsLevelDiscountsDimensionsConfigurationItem } from '@streaming-projects/user-commit-details/commit-custom-discounts/dimensions-configuration/CommitMetricsLevelDiscountsDimensionsConfigurationItem';

export const CommitMetricsDimensionsListContainer = ({ metricDimensionRefs, readOnlyMode }) => {
  const orgData = useContext(SPOrgContext);
  const { rowValues, index } = useContext(FieldArrayGridConfigItemContext);
  const metricName = rowValues[METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_NAME_BACKEND_NAME];
  const pathToFollowInValues = [
    CUSTOM_DISCOUNTS_JSON_BACKEND_NAME,
    `${METRICS_LEVEL_DISCOUNTS_CONFIG_ARRAY_BACKEND_NAME}[${index}]["${METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_ARRAY_BACKEND_NAME}"]`,
  ];
  const rateCardVersion = orgData?.org[RATE_CARD_BACKEND_NAME];

  return (
    <DataFetcherContainer
      dataFetchingArgs={useMemo(
        () => [
          {
            metricName: metricName,
            rateCardVersion: rateCardVersion,
          },
          {
            skip: !metricName,
          },
        ],
        [rateCardVersion, metricName]
      )}
      dataFetchingFunction={useCallback(useGetDependentDimensionsQuery, [])}
    >
      {(dependentDimensions) => {
        const dependentDimensionsToUse = (dependentDimensions?.dependent_dimensions ?? []).filter(
          (x) => x.name !== CONNECTOR_SMT_TYPE_BACKEND_NAME
        );
        const columnConfig = getDependentDimensionsColumnConfig(
          dependentDimensionsToUse,
          metricName,
          true,
          false,
          false // CE2 does not have a concept of Usage column
        );

        metricDimensionRefs.current.dimensionColumnsConfiguration = columnConfig;

        return (
          <>
            <FieldArrayGrid
              addDividerBetweenRows={false}
              columnsConfiguration={columnConfig}
              disabled={readOnlyMode}
              functionToExecuteOnceArrayHelpersAreAvailable={(arrayHelpers) => {
                metricDimensionRefs.current.dimensionArrayHelpers = arrayHelpers;
              }}
              gridName="New Dimensions Configuration For the above metric"
              needsAddButton={false}
              pathToFollowInValues={pathToFollowInValues}
              verticalSpaceBetweenHeadersAndRows={5}
            >
              {(arrayHelpers, row, isCurrentlyBeingEdited, index, allRows, readOnlyMode) => {
                return (
                  <FieldArrayGridConfigItemContext.Provider
                    value={{
                      allRows,
                      arrayHelpers,
                      columnsConfiguration: columnConfig,
                      index,
                      isCurrentlyBeingEdited,
                      pathToFollowInValues: pathToFollowInValues,
                      rowValues: row,
                      readOnlyMode,
                      parentContext: rowValues,
                      metricDimensionRefs: metricDimensionRefs,
                    }}
                  >
                    <CommitMetricsLevelDiscountsDimensionsConfigurationItem />
                  </FieldArrayGridConfigItemContext.Provider>
                );
              }}
            </FieldArrayGrid>
          </>
        );
      }}
    </DataFetcherContainer>
  );
};
